/** Instancia axios global: baseURL desde `VITE_API_URL`, inyección del Bearer token, refresh de sesión en 401 con cola de requests encoladas y logout ante token vencido. */
import axios from "axios";
import { useAuthStore } from "@/stores/authStore";
import { API_ENDPOINTS } from "./apiEndpoints";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null): void => {
  failedQueue.forEach((prom) => {
    if (error || !token) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const SKIP_AUTH_URLS = [API_ENDPOINTS.LOGIN, API_ENDPOINTS.POST_REFRESCAR_TOKEN];

axiosInstance.interceptors.request.use(
  (config) => {
    const { loggedUserInnerwork } = useAuthStore.getState();
    const token = loggedUserInnerwork?.tokenDatos?.accessToken;

    if (token && !SKIP_AUTH_URLS.some((url) => config.url?.includes(url))) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url === API_ENDPOINTS.POST_REFRESCAR_TOKEN) {
      const { logout } = useAuthStore.getState();
      logout();
      window.location.href = "/";
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { loggedUserInnerwork, refreshTokens } = useAuthStore.getState();
    const currentAccessToken = loggedUserInnerwork?.tokenDatos?.accessToken;
    const currentRefreshToken = loggedUserInnerwork?.tokenDatos?.refreshToken;

    if (!currentRefreshToken || !currentAccessToken) {
      isRefreshing = false;
      const { logout } = useAuthStore.getState();
      logout();
      window.location.href = "/";
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/${API_ENDPOINTS.POST_REFRESCAR_TOKEN}`,
        {
          accessToken: currentAccessToken,
          refreshToken: currentRefreshToken,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const { accessToken: newAccess, refreshToken: newRefresh } = response.data;

      refreshTokens(newAccess, newRefresh);
      processQueue(null, newAccess);

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      const { logout } = useAuthStore.getState();
      logout();
      window.location.href = "/";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
